import { useEffect } from 'react'
import { markExperimentExposed } from '@/core/experiments'
import {
  useLifecycleExperiment,
  usePostDecisionUser,
} from '@/core/feature-flags'

/**
 * Enters this user into the Collection experiment's exposed population.
 *
 * Gated on the plan's allowlist and nothing else — not the arm, and not whether the tab rendered.
 * Only the treatment can satisfy either, so filtering on them drops the control group and biases
 * whatever the test measures. This is the opposite rule to the one that decides whether the tab
 * shows, and the two are deliberately different.
 *
 * An absent `collection` entry is the membership test: the flag never fired for this user, and
 * reporting anyway counts somebody the experiment does not contain.
 *
 * No arm is sent. The server assigned this one itself and can read it back from its own record;
 * an arm we merely read from config is a claim that can disagree with the assignment it came from.
 *
 * Mirrors leaply-android's `reportCollection`, which reports on plan entry for the same reason.
 */
export function useCollectionExposure(enabled = true): void {
  const user = usePostDecisionUser()
  const userId = user?.id
  // Resolved the way `useActivePlan` resolves it, down to the fallback: the tab reads that
  // answer, and a plan id neither of them agreed on shows the Collection to someone the
  // experiment never counted.
  const activePlan =
    user?.availablePlans.find((plan) => plan.id === user.activePlanId) ??
    user?.availablePlans[0]
  const activePlanId = activePlan?.id
  const hasCollection = activePlan?.hasCollection === true
  const experiment = useLifecycleExperiment('collection')
  const experimentName = experiment?.name

  useEffect(() => {
    if (!enabled) return
    if (!userId || !activePlanId || !experimentName) return
    // The allowlist is the eligibility. The surface carries no plan anchor of its own.
    if (!hasCollection) return
    markExperimentExposed({ userId, planId: activePlanId, experimentName })
  }, [enabled, userId, activePlanId, hasCollection, experimentName])
}
